import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  QrCodeScanner,
  CameraAlt,
  FlashOn,
  FlashOff,
  LocalBar,
  MoreVert,
  ShoppingCart,
  Settings,
  Logout,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { logoutLocal } from '../../store/slices/authSlice';
import { AppLayout } from '../../components/common/AppLayout';
import { AppBottomNavigation } from '../../components/common/BottomNavigation';
import { ROUTES } from '../../../core/config/constants';
import { Html5QrcodeScanner, Html5Qrcode } from 'html5-qrcode';
import { bottlesApi } from '../../../data/api/bottles-api';

export const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { bottles, isLoading } = useAppSelector((state) => state.bottles);
  const { totalBottles } = useAppSelector((state) => state.bottles);

  const [isScanning, setIsScanning] = useState(false);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerDivRef = useRef<HTMLDivElement>(null);

  const startScanning = async () => {
    try {
      if (!scannerDivRef.current) return;
      
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;
      
      const qrCodeSuccessCallback = async (decodedText: string, decodedResult: any) => {
        if (isProcessing) return;
        
        console.log('QR Code détecté:', decodedText);
        setIsProcessing(true);
        
        try {
          // Extraire l'ID de la bouteille du QR code
          // Supposons que le QR code contient directement l'ID de la bouteille
          const bouteilleId = parseInt(decodedText, 10);
          
          if (isNaN(bouteilleId)) {
            throw new Error('QR Code invalide');
          }
          
          // Appeler l'API pour récupérer les détails de la bouteille
          const bottle = await bottlesApi.findBottleById(bouteilleId);
          
          // Arrêter le scan
          await stopScanning();
          
          // Naviguer vers la page de détail
          navigate(`/bottles/${bottle.id}`);
        } catch (err: any) {
          console.error('Erreur lors de la recherche de la bouteille:', err);
          setError(err.message || 'Bouteille non trouvée');
          setIsProcessing(false);
        }
      };
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };
      
      await scanner.start(
        { facingMode: 'environment' },
        config,
        qrCodeSuccessCallback,
        undefined
      );
      
      setIsScanning(true);
      
      // Vérifier si le flash est disponible
      const cameras = await Html5Qrcode.getCameras();
      if (cameras && cameras.length > 0) {
        // Le flash peut être disponible sur certains appareils
        setHasFlash(true);
      }
    } catch (err: any) {
      console.error('Erreur lors du démarrage du scanner:', err);
      setError('Impossible d\'accéder à la caméra. Veuillez vérifier les permissions.');
    }
  };

  const stopScanning = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current = null;
      }
    } catch (err) {
      console.error('Erreur lors de l\'arrêt du scanner:', err);
    }
    setIsScanning(false);
    setFlashEnabled(false);
    setIsProcessing(false);
  };

  const toggleFlash = async () => {
    if (scannerRef.current) {
      try {
        const torchState = flashEnabled ? false : true;
        await scannerRef.current.applyVideoConstraints({
          advanced: [{ torch: torchState } as any]
        });
        setFlashEnabled(torchState);
      } catch (err) {
        console.error('Error toggling flash:', err);
        setError('Impossible de contrôler le flash sur cet appareil.');
      }
    }
  };


  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logoutLocal());
    handleMenuClose();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const handleSettings = () => {
    navigate(ROUTES.SETTINGS);
    handleMenuClose();
  };

  const handleCartClick = () => {
    window.open('https://vinosk.fr/api/payment/shop/tag', '_blank');
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(console.error);
      }
    };
  }, []);

  return (
    <Box sx={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" elevation={0}>
        <Toolbar>
          <Box
            component="img"
            src="https://vinosk.fr/images/logo-white.png"
            alt="Vinosk Logo"
            sx={{
              height: 32,
              width: 32,
              mr: 2,
            }}
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Recherche
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              size="large"
              color="inherit"
              onClick={handleCartClick}
              sx={{ mr: 1 }}
              title="Panier"
            >
              <ShoppingCart />
            </IconButton>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              onClick={handleMenuOpen}
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box 
        sx={{ 
          flex: 1,
          maxWidth: { xs: '100%', sm: 450 },
          margin: '0 auto',
          p: { xs: 2, sm: 3 },
          backgroundColor: '#000000',
          color: '#FFFFFF',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Typography 
          variant="h5" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            color: '#FFFFFF',
            mb: 4,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            textAlign: 'center'
          }}
        >
          Trouvez une bouteille
        </Typography>

        {/* QR Code Scanner Area */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: 300,
            backgroundColor: '#333333',
            borderRadius: 4,
            mb: 4,
            overflow: 'hidden',
            border: '2px solid #641835'
          }}
        >
          <div
            id="qr-reader"
            ref={scannerDivRef}
            style={{
              width: '100%',
              height: '100%',
              position: 'relative'
            }}
          >
            {!isScanning && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: '#641835'
                }}
              >
                <QrCodeScanner sx={{ fontSize: 80, mb: 2 }} />
              </Box>
            )}
          </div>
          
          {isScanning && (
            <>
              {/* Flash Toggle */}
              {hasFlash && (
                <IconButton
                  onClick={toggleFlash}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: flashEnabled ? '#FFA726' : '#FFFFFF',
                    zIndex: 10,
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.8)',
                    },
                  }}
                >
                  {flashEnabled ? <FlashOn /> : <FlashOff />}
                </IconButton>
              )}

              {/* Stop Scanning Button */}
              <IconButton
                onClick={stopScanning}
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  backgroundColor: 'rgba(255,0,0,0.7)',
                  color: '#FFFFFF',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'rgba(255,0,0,0.9)',
                  },
                }}
              >
                <CameraAlt />
              </IconButton>
            </>
          )}
        </Box>

        {/* Description */}
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#CCCCCC',
            mb: 4,
            textAlign: 'center',
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}
        >
          Scannez le QR code d'une bouteille Vinosk pour afficher ses détails et informations
        </Typography>

        {/* Scan Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={isScanning ? stopScanning : startScanning}
          startIcon={<QrCodeScanner />}
          sx={{
            backgroundColor: '#641835',
            color: '#FFFFFF',
            py: 2,
            mb: 3,
            borderRadius: 3,
            fontSize: '1.1rem',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#8B2942',
            },
            '&:disabled': {
              backgroundColor: '#444444',
              color: '#AAAAAA',
            },
          }}
          disabled={isLoading || isProcessing}
        >
          {isScanning ? 'Arrêter le scan' : 'Scanner un QR code'}
        </Button>

        {/* Info Message */}
        <Box
          sx={{
            backgroundColor: 'rgba(100, 24, 53, 0.1)',
            border: '1px solid #641835',
            borderRadius: 3,
            p: 2,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#CCCCCC',
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.85rem'
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                backgroundColor: '#641835',
                borderRadius: '50%',
                mr: 2,
                flexShrink: 0
              }}
            />
            Seules les bouteilles avec un tag QR Vinosk peuvent être trouvées
          </Typography>
        </Box>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSuccessMessage(null)}
            severity="success"
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </Box>

      <AppBottomNavigation />

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleSettings}>
          <Settings sx={{ mr: 1 }} />
          Paramètres
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <Logout sx={{ mr: 1 }} />
          Déconnexion
        </MenuItem>
      </Menu>
    </Box>
  );
};